/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Divider,
  Button,
  Input,
  Select,
  Center,
  Badge,
} from "@chakra-ui/react";
import { FaTruck, FaBoxOpen, FaMapMarkerAlt, FaCheckCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";

export default function Shipping() {
  const { i18n } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();
  const [selectedOption, setSelectedOption] = useState("");

  const shippingOptions = [
    {
      id: 1,
      company: "Aramex",
      type: "Express",
      price: "7000 IQD",
      time: "1-2 أيام",
      icon: FaTruck,
    },
    {
      id: 2,
      company: "Zajil",
      type: "Standard",
      price: "4000 IQD",
      time: "3-5 أيام",
      icon: FaBoxOpen,
    },
    {
      id: 3,
      company: "DHL",
      type: "International",
      price: "15000 IQD",
      time: "5-7 أيام",
      icon: FaTruck,
    },
  ];

  return (
    <PageContainer title={isArabic ? "إدارة الشحن 🚚" : "Shipping Management 🚚"}>
      <UnifiedCard mb={10} maxW="700px" mx="auto" hoverable={false}>
        <VStack spacing={4} align="stretch">
          <Text color={colors.textColor} fontWeight="bold" fontSize="lg">
            {isArabic ? "إعداد تفاصيل الشحن" : "Shipping Details Setup"}
          </Text>

          <Divider />

          <HStack spacing={4} flexWrap="wrap">
            <Box flex="1" minW="200px">
              <Text color={colors.textColor} mb={2} fontWeight="medium">
                {isArabic ? "المدينة" : "City"}
              </Text>
              <Input 
                placeholder={isArabic ? "أدخل المدينة" : "Enter city"} 
                bg={colors.inputBg}
                color={colors.textColor}
                borderColor={colors.inputBorder}
                _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                _hover={{ borderColor: colors.inputFocus }}
                _dark={{ bg: colors.inputBg, color: colors.textColor }}
              />
            </Box>
            <Box flex="1" minW="200px">
              <Text color={colors.textColor} mb={2} fontWeight="medium">
                {isArabic ? "الدولة" : "Country"}
              </Text>
              <Input 
                placeholder={isArabic ? "أدخل الدولة" : "Enter country"} 
                bg={colors.inputBg}
                color={colors.textColor}
                borderColor={colors.inputBorder}
                _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                _hover={{ borderColor: colors.inputFocus }}
                _dark={{ bg: colors.inputBg, color: colors.textColor }}
              />
            </Box>
          </HStack>

          <Box>
            <Text color={colors.textColor} mb={2} fontWeight="medium">
              {isArabic ? "شركة الشحن" : "Shipping Company"}
            </Text>
            <Select
              placeholder={isArabic ? "اختر شركة الشحن" : "Select shipping company"}
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              bg={colors.inputBg}
              color={colors.textColor}
              borderColor={colors.inputBorder}
              _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
              _hover={{ borderColor: colors.inputFocus }}
              _dark={{ bg: colors.inputBg, color: colors.textColor }}
            >
              {shippingOptions.map((opt) => (
                <option key={opt.id} value={opt.company}>
                  {opt.company} ({opt.type})
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text color={colors.textColor} mb={2} fontWeight="medium">
              {isArabic ? "العنوان التفصيلي" : "Detailed Address"}
            </Text>
            <Input
              placeholder={isArabic ? "مثال: شارع الجامعة، مقابل مول العرب" : "Example: University Street, opposite Arab Mall"}
              bg={colors.inputBg}
              color={colors.textColor}
              borderColor={colors.inputBorder}
              _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
              _hover={{ borderColor: colors.inputFocus }}
              _dark={{ bg: colors.inputBg, color: colors.textColor }}
            />
          </Box>

          <Button 
            bg={colors.buttonBg}
            color={colors.buttonText}
            _hover={{ bg: colors.buttonHover, transform: "translateY(-2px)", boxShadow: "lg" }}
            borderRadius="xl" 
            mt={3}
            fontWeight="semibold"
            transition="all 0.3s"
          >
            {isArabic ? "حفظ الإعدادات" : "Save Settings"}
          </Button>
        </VStack>
      </UnifiedCard>

      <Text
        mb={6}
        textAlign="center"
        color={colors.mainColor}
        fontSize={{ base: "xl", md: "2xl" }}
        fontWeight="bold"
      >
        {isArabic ? "خيارات الشحن المتاحة" : "Available Shipping Options"}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
        {shippingOptions.map((opt) => (
          <UnifiedCard
            key={opt.id}
            icon={opt.icon}
            title={opt.company}
            badge={opt.type}
          >
            <VStack align="start" spacing={2} mt={3}>
              <Text color={colors.subText}>💰 {isArabic ? "السعر:" : "Price:"} {opt.price}</Text>
              <Text color={colors.subText}>⏱️ {isArabic ? "المدة:" : "Duration:"} {opt.time}</Text>
              <Text color={colors.subText}>
                <FaMapMarkerAlt
                  style={{
                    display: "inline",
                    marginLeft: "5px",
                    color: colors.mainColor,
                  }}
                />
                {isArabic ? "التوصيل لجميع المحافظات" : "Delivery to all provinces"}
              </Text>
            </VStack>

            <Divider my={4} borderColor={colors.dividerColor} />

            <Center>
              <Button
                size="sm"
                leftIcon={<FaCheckCircle />}
                bg={colors.mainColor}
                color={colors.buttonText}
                _hover={{ bg: colors.mainColorHover, transform: "translateY(-2px)", boxShadow: "md" }}
                borderRadius="xl"
                variant="solid"
                fontWeight="semibold"
                transition="all 0.3s"
              >
                {isArabic ? "اختيار" : "Select"}
              </Button>
            </Center>
          </UnifiedCard>
        ))}
      </SimpleGrid>
    </PageContainer>
  );
}
